// FilterComponent.jsx
import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Collapse,
  Badge,
  Stack,
  Fade,
  alpha,
  useTheme,
} from "@mui/material";
import {
  SearchOutlined as SearchIcon,
  RestartAlt as ResetIcon,
  FilterAltOutlined as FilterIcon,
  ExpandMoreOutlined as ArrowIcon,
  EventNote as EventIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { QK } from "../../base/qk";
import { searchClients } from "../../api/services/Client";
import { searchCandidates } from "../../api/services/candidate";
import { getLocations, getSubLocations } from "../../api/services/scheduling";
import CustomSelect from "../../components/FormFields/CustomSelect/CustomSelect";
import SearchableSelect from "../../components/FormFields/CustomSelect/SearchableSelect";
import CustomDateField from "../../components/FormFields/CustomDateField/CustomDateField";

const FilterComponent = ({ setFilters, totalRecords }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const [clientSearch, setClientSearch] = useState("");
  const [careerSearch, setCareerSearch] = useState("");
  const [clientObj, setClientObj] = useState(null);
  const [careerObj, setCareerObj] = useState(null);
  const [location, setLocation] = useState("");
  const [area, setArea] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [careerAssigned, setCareerAssigned] = useState("");

  const activeCount = [clientObj, careerObj, location, area, startDate, endDate, careerAssigned].filter(Boolean).length;

  const { data: clients, isLoading: clientsLoading } = useQuery(
    [QK.searchClients, clientSearch],
    () => searchClients(clientSearch),
    { enabled: clientSearch.length > 1 }
  );

  const { data: careers, isLoading: careersLoading } = useQuery(
    [QK.searchCareers, careerSearch],
    () => searchCandidates(careerSearch),
    { enabled: careerSearch.length > 1 }
  );

  const { data: locations } = useQuery([QK.location], () => getLocations());
  const { data: sublocations } = useQuery(
    [QK.subLocations, location],
    () => getSubLocations(location),
    { enabled: !!location }
  );

  const clientOptions = clients?.map((c) => ({
    value: c.id,
    label: `${c.first_name} ${c.last_name}`,
  })) || [];

  const careerOptions = careers?.map((c) => ({
    value: c.id,
    label: `${c.first_name} ${c.last_name}`,
  })) || [];

  const assignmentOptions = [
    { value: "true", label: "Career Assigned" },
    { value: "false", label: "Career Not Assigned" },
  ];

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 400);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFilters({
      client: clientObj?.value || "",
      career: careerObj?.value || "",
      location,
      area,
      startDate,
      endDate,
      careerAssigned,
    });
  };

  const handleReset = () => {
    setClientObj(null);
    setCareerObj(null);
    setLocation("");
    setArea("");
    setStartDate("");
    setEndDate("");
    setCareerAssigned("");
    setClientSearch("");
    setCareerSearch("");
    setFilters({});
  };

  const locationOptions = locations?.map((loc) => ({
    value: loc.id,
    label: loc.name,
  })) || [];

  const areaOptions = sublocations?.map((sub) => ({
    value: sub.id,
    label: sub.name,
  })) || [];

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ width: "100%", mb: 3 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.2,
          bgcolor: isOpen ? alpha(theme.palette.primary.main, 0.03) : "#FAFBFC",
          borderRadius: isOpen ? "12px 12px 0 0" : "12px",
          border: "1px solid",
          borderColor: isOpen ? theme.palette.primary.main : "#F2F4F7",
          borderBottom: isOpen ? "none" : "1px solid #F2F4F7",
          transition: theme.transitions.create(["background-color", "border-color"], {
            duration: 300,
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EventIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#1A1A2E",
                fontSize: "0.85rem",
                letterSpacing: "-0.02em",
              }}
            >
              Weekly Visit Plans
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 0.75,
                py: 0.15,
                borderRadius: "4px",
                bgcolor: "#F2F4F7",
                border: "1px solid #E4E7EC",
              }}
            >
              <Typography
                sx={{
                  color: "#475467",
                  fontWeight: 600,
                  fontSize: "0.6rem",
                  letterSpacing: "0.03em",
                }}
              >
                {totalRecords?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Box>
          {activeCount > 0 && (
            <Fade in={true}>
              <Badge
                badgeContent={activeCount}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontWeight: 700,
                    fontSize: "0.5rem",
                    height: 16,
                    minWidth: 16,
                  },
                }}
              >
                <FilterIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
              </Badge>
            </Fade>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            sx={{
              fontSize: "0.5rem",
              fontWeight: 700,
              color: isOpen ? theme.palette.primary.main : "#98A2B3",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {isOpen ? "Search Mode" : "Hover to Filter"}
          </Typography>
          <ArrowIcon
            sx={{
              fontSize: 16,
              color: isOpen ? theme.palette.primary.main : "#98A2B3",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: theme.transitions.create("transform", { duration: 400 }),
            }}
          />
        </Stack>
      </Stack>

      <Collapse in={isOpen}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: "#FFFFFF",
            borderRadius: "0 0 12px 12px",
            border: "1px solid",
            borderColor: theme.palette.primary.main,
            borderTop: "none",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ flex: '1 1 160px', minWidth: '140px' }}>
                <SearchableSelect
                  options={clientOptions}
                  isLoading={clientsLoading}
                  onChange={setClientObj}
                  value={clientObj}
                  placeholder="Search Service User..."
                  onInputChange={setClientSearch}
                  inputValue={clientSearch}
                  label="Service User"
                  size="small"
                />
              </Box>

              <Box sx={{ flex: '1 1 160px', minWidth: '140px' }}>
                <SearchableSelect
                  options={careerOptions}
                  isLoading={careersLoading}
                  onChange={setCareerObj}
                  value={careerObj}
                  placeholder="Search employee..."
                  onInputChange={setCareerSearch}
                  inputValue={careerSearch}
                  label="Employee"
                  size="small"
                />
              </Box>

              <Box sx={{ flex: '1 1 130px', minWidth: '120px' }}>
                <CustomDateField
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="DD/MM/YYYY"
                  label="Date From"
                />
              </Box>

              <Box sx={{ flex: '1 1 130px', minWidth: '120px' }}>
                <CustomDateField
                  name="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="DD/MM/YYYY"
                  label="Date To"
                />
              </Box>

              <Box sx={{ flex: '1 1 130px', minWidth: '120px' }}>
                <CustomSelect
                  options={locationOptions}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Select location"
                  fullWidth
                  size="small"
                  label="Location"
                />
              </Box>

              <Box sx={{ flex: '1 1 130px', minWidth: '120px' }}>
                <CustomSelect
                  options={areaOptions}
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder="Select area"
                  fullWidth
                  size="small"
                  label="Area"
                />
              </Box>

              <Box sx={{ flex: '1 1 130px', minWidth: '120px' }}>
                <CustomSelect
                  options={assignmentOptions}
                  value={careerAssigned}
                  onChange={(e) => setCareerAssigned(e.target.value)}
                  placeholder="Select assignment"
                  fullWidth
                  size="small"
                  label="Assignment"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flex: '1 1 130px', minWidth: '120px' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  startIcon={<ResetIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{
                    height: "32px",
                    borderRadius: "6px",
                    color: "#667085",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    borderColor: "#D0D5DD",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                      borderColor: "#98A2B3",
                    },
                  }}
                >
                  Clear
                </Button>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    textTransform: "none",
                    bgcolor: theme.palette.primary.main,
                    boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: `0px 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Collapse>
    </Box>
  );
};

export default FilterComponent;